import { isNotDefined } from '@sgme/fp';
import type { ColTypeDef, ValueFormatterParams } from 'ag-grid-community';
import { formatToPercent, usePercentColumnType } from 'components/shared/Blotter/Grid/Hooks/usePercentColumnType';
import { getCurrencySymbol } from 'components/shared/NewAmountInput/CurrencySymbol';
import { useSPIntl } from 'hooks/useIntl';
import { useCallback, useMemo } from 'react';
import type { FormatNumberOptions } from 'react-intl';
import type { SecondaryPriceType } from 'state/secondaryMarket/productSearch';

export type PriceTypeInfo = {
  priceType?: SecondaryPriceType | string;
  currency: string | undefined;
};

export function usePercentOrAmountColumnType<T>(getPriceTypeInfo: (data: T) => PriceTypeInfo): ColTypeDef<T> {
  const { formatNumber } = useSPIntl();
  const percentColumn = usePercentColumnType<T>(4);

  const valueFormatter = useCallback(
    (params: ValueFormatterParams) => {
      return formatToPercentOrAmount(params.value, getPriceTypeInfo(params.data), formatNumber);
    },
    [formatNumber, getPriceTypeInfo],
  );
  return useMemo<ColTypeDef<T>>(() => ({ ...percentColumn, valueFormatter }), [percentColumn, valueFormatter]);
}

export function formatToPercentOrAmount(
  value: number | null | undefined,
  priceTypeInfo: PriceTypeInfo,
  formatNumber: (value: Parameters<Intl.NumberFormat['format']>[0], opts?: FormatNumberOptions) => string,
): string {
  if (isNotDefined(value)) {
    return '';
  }
  if (priceTypeInfo.priceType === undefined) {
    return formatNumber(value);
  }
  switch (priceTypeInfo?.priceType) {
    case 'Percent':
      return formatToPercent(value, 4, formatNumber);
    case 'Amount':
      return formatToAmount(value, priceTypeInfo.currency, formatNumber);
    default:
      throw new Error(`price type is not supported: ${priceTypeInfo?.priceType}`);
  }
}

export function formatToAmount(
  value: number | bigint | null | undefined,
  currency: string | undefined,
  formatNumber: (value: Parameters<Intl.NumberFormat['format']>[0], opts?: FormatNumberOptions) => string,
) {
  if (isNotDefined(value) || currency === undefined) {
    return '';
  }
  return `${formatNumber(value, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })} ${getCurrencySymbol(currency!)}`;
}
